import axios from "axios";
import { apiVariable } from "./apiVariable";
import { PublicAssetType } from "../helpers/publicAssetDocuments";

export interface PublicAssetFile {
  id: string;
  fileName: string;
  type?: string | null;
  startDate?: string | null;
  expirationDate?: string | null;
}

// An independent client keeps public requests free of authentication headers.
const publicClient = axios.create({ withCredentials: false });

export const fetchAvailableAssetFiles = async (
  assetType: PublicAssetType,
  folderId: string,
  signal?: AbortSignal,
): Promise<PublicAssetFile[]> => {
  const endpoint =
    assetType === "truck"
      ? "getAvailableTruckFiles"
      : "getAvailableTrailerFiles";
  const { data } = await publicClient.get<PublicAssetFile[]>(
    `${apiVariable}/${endpoint}/${folderId}`,
    { signal },
  );
  if (!Array.isArray(data)) throw new Error("Invalid document response.");
  return data;
};

export const getPublicDocumentError = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    if (error.response?.status === 401 || error.response?.status === 403) {
      return "This document is not available for public access. Please contact the person who shared this link.";
    }
    if (error.response?.status === 404 || error.response?.status === 410) {
      return "This document or folder is no longer available.";
    }
  }
  return "Unable to load this document. Please try again.";
};

export const fetchPublicAssetFile = async (
  file: PublicAssetFile,
  unitNumber: string,
  signal?: AbortSignal,
): Promise<Blob> => {
  const match = file.fileName?.match(/\.([a-z0-9]+)$/i);
  const preferred = match?.[1]?.toLowerCase();
  const extensions = Array.from(
    new Set(
      [preferred, "pdf", "jpg", "jpeg", "png"].filter(
        (ext): ext is string => !!ext,
      ),
    ),
  );

  for (const [index, extension] of extensions.entries()) {
    try {
      const { data } = await publicClient.get<Blob>(
        `${apiVariable}/public/download/${encodeURIComponent(file.id)}/assetFolder/${extension}`,
        {
          responseType: "blob",
          signal,
          params: { fileName: file.fileName, unitNumber },
        },
      );
      // Do not embed HTML/SVG served by an unexpected endpoint as a document.
      const contentType = data.type.split(";")[0].toLowerCase();
      if (
        [
          "text/html",
          "application/xhtml+xml",
          "image/svg+xml",
          "application/json",
        ].includes(contentType)
      ) {
        throw new Error("Unexpected document response.");
      }
      const mimeTypes: Record<string, string> = {
        pdf: "application/pdf",
        jpg: "image/jpeg",
        jpeg: "image/jpeg",
        png: "image/png",
      };
      // Some downloads use application/octet-stream even for PDFs and images.
      return mimeTypes[extension]
        ? new Blob([data], { type: mimeTypes[extension] })
        : data;
    } catch (error) {
      const status = axios.isAxiosError(error)
        ? error.response?.status
        : undefined;
      // Match the existing API's missing-file behavior. Never retry authorization failures.
      if (![404, 500].includes(status ?? 0) || index === extensions.length - 1)
        throw error;
    }
  }
  throw new Error("Document unavailable.");
};
